/**
 * exports.ts — the Exports tab's rules, pure (§31 E-2, E-5..E-8 as ruled;
 * §31.4 for what each field is worth to a reader). The wire is
 * GET /v1/exports (owner/admin): one row per export ATTEMPT — who asked,
 * what was asked, when, and, once the stream ended, how many lines and
 * bytes went out. Nothing here fabricates: every sentence is built from a
 * value the read path exposes, and a value it does not expose reads as
 * "not recorded", never as 0.
 *
 *   E-2  a non-owner/admin sees a SENTENCE and no request is made — fail
 *        closed on /v1/me (null = no session truth = not allowed); the
 *        server 403 stays the boundary.
 *   E-5  the definition renders as a sentence from the builder's OWN
 *        vocabulary (reportBuilder.ts); an unknown slug renders verbatim,
 *        never guessed; a definitionVersion this bundle does not speak (≠ 1)
 *        yields NO sentence — the raw JSON is the only honest render.
 *        §35 (ruled 2026-09-20): an id-valued filter prints the LABEL the
 *        server snapshotted beside it at export time (definition.labels,
 *        keyed by the filter slug) when one is present, and the raw id when
 *        none is — never a live lookup on this screen and never a backfill,
 *        both of which would put today's name on yesterday's record (§11).
 *   E-6  rowCount is "rows in the file", never records; recordCount is the
 *        records, and null reads "not recorded" (both live rows are null).
 *   E-7  an open row is "never confirmed complete" — Complete runs on the
 *        request context after the last byte, so a delivered file can leave
 *        one; the word "failed" appears nowhere.
 *   E-8  the screen pages by the wire's cursor ("Load older exports") and
 *        says "Showing N of Total" from page.total, the Builder's shape —
 *        never from a bounded guess.
 */
import type { DataExport, SalesReportDefinition } from '../../../../api/types'
import type { MeResponse } from '../../types'
import { OBJECT_OPTIONS, DIMENSIONS, MEASURES, RANGE_FIELDS, groupLabel } from '../sales/reportBuilder'
import { fmtDateTime } from '../../api'

/** The page size the tab asks for — the route's own cap. */
export const EXPORTS_PAGE = 200

/** The vocabulary version this bundle can turn into a sentence. */
export const KNOWN_DEFINITION_VERSION = 1

/** E-2: owner or admin, decided from /v1/me — the session's truth for the
 *  role, the same fail-closed read the approvals decide buttons use. */
export function canReadExports(me: Pick<MeResponse, 'role'> | null | undefined): boolean {
  return me?.role === 'owner' || me?.role === 'admin'
}

/** What a non-owner/admin reads instead of a table — the tab exists, the
 *  record exists, and it is not theirs to read (the precedent's shape:
 *  told by name, never a 404 that pretends otherwise). */
export const EXPORTS_GATE_SENTENCE = 'Export records are visible to workspace owners and admins.'

/** Surface slugs the read path can emit today, labelled; anything else is
 *  the slug itself (the B-5 posture — a second surface adds its slug to
 *  0128's CHECK by migration, and its label here in the same slice). */
const SURFACE_LABELS: Record<string, string> = { 'sales.reports.csv': 'Sales report (CSV)' }
export function surfaceLabel(surface: string): string {
  return SURFACE_LABELS[surface] ?? surface
}

/** The AN-4 ladder for who exported: the resolved name → "User <id8>" →
 *  "Unknown user" (the users row was deleted, so user_id is NULL). An
 *  email-shaped name is refused as the history strip refuses it. */
const EMAIL_SHAPED = /@/
export function exportActor(row: Pick<DataExport, 'userId' | 'userDisplayName'>): string {
  if (!row.userId) return 'Unknown user'
  const name = (row.userDisplayName ?? '').trim()
  if (name === '' || EMAIL_SHAPED.test(name)) return `User ${row.userId.slice(0, 8)}`
  return name
}

export type ExportStatus = 'completed' | 'unconfirmed'
export function exportStatus(row: Pick<DataExport, 'completedAt'>): ExportStatus {
  return row.completedAt ? 'completed' : 'unconfirmed'
}
export const STATUS_LABEL: Record<ExportStatus, string> = {
  completed: 'Completed',
  unconfirmed: 'Never confirmed complete',
}
/** E-7, the audit's point, in a sentence rather than a dash. */
export const UNCONFIRMED_NOTE = 'The file may have been delivered in part or in full; the record cannot say.'

/** E-6: the CSV's line count, labelled as the file's shape. Null on an
 *  unconfirmed row — the count is stamped at completion. */
export function rowsInFile(row: Pick<DataExport, 'rowCount'>): string {
  if (row.rowCount === null) return '—'
  return `${row.rowCount} ${row.rowCount === 1 ? 'row' : 'rows'} in the file`
}

/** The RECORDS the population held (0130). Null is "not recorded" — the
 *  query did not run, or the row predates the column — and is never 0. */
export function recordCountText(row: Pick<DataExport, 'recordCount'>): string {
  if (row.recordCount === null) return 'not recorded'
  return `${row.recordCount} ${row.recordCount === 1 ? 'record' : 'records'}`
}

/** The fence's drop count, analyst-grade: a count measure has no fence, so
 *  its null reads as no figure — never "0 excluded". */
export function excludedText(row: Pick<DataExport, 'excludedNonGbp'>): string {
  if (row.excludedNonGbp === null) return '—'
  return `${row.excludedNonGbp} excluded (non-GBP)`
}

/** Bytes as the operator reads them beside nginx and the file on disk. */
export function bytesText(row: Pick<DataExport, 'bytes'>): string {
  if (row.bytes === null) return '—'
  if (row.bytes < 1024) return `${row.bytes} B`
  return `${(row.bytes / 1024).toFixed(1)} KB`
}

/** "Started 12 Sep 2026, 12:13 by Klem Klem" — the reader's first line. */
export function startedLine(row: Pick<DataExport, 'startedAt' | 'userId' | 'userDisplayName'>): string {
  return `Started ${fmtDateTime(row.startedAt)} by ${exportActor(row)}`
}

// ── E-5: the definition as a sentence ────────────────────────────────────────

const FILTER_LABELS: Record<string, string> = {
  stage: 'Stage', assignedEmployeeId: 'Owner (employee)', assignedTo: 'Owner', accountId: 'Account',
  source: 'Source', priority: 'Priority', forecastCategory: 'Forecast category',
  forecastCategorySource: 'Forecast category source', status: 'Status', activityType: 'Type',
  contactId: 'Contact', dealId: 'Deal',
}

/** §35: the id-valued keys whose stored label prints in place of the id.
 *  Only these — a label on any other key is not the server's to write and
 *  is ignored, so the sentence never says more than the row recorded. */
const ID_FILTER_KEYS: ReadonlySet<string> = new Set(['assignedEmployeeId', 'accountId', 'contactId', 'dealId'])

/** A filter value in words: the closed vocabularies get their label through
 *  the builder's own groupLabel (stage, forecast category, source, priority);
 *  an id-valued filter prints its snapshotted label when the row carries one
 *  and the raw id otherwise (§35); everything else is the stored text
 *  verbatim. */
function filterValue(key: string, value: string, labels?: Record<string, string>): string {
  switch (key) {
    case 'stage': return groupLabel('stage', value)
    case 'forecastCategory': return value === 'uncategorised' ? 'Uncategorised' : groupLabel('forecast_category', value)
    case 'source': return groupLabel('source', value)
    case 'priority': return groupLabel('priority', value)
    default: return ID_FILTER_KEYS.has(key) && labels?.[key] ? labels[key] : value
  }
}

function objectLabel(object: string): string {
  return OBJECT_OPTIONS.find(o => o.value === object)?.label ?? object
}
function dimensionLabelFor(object: string, groupBy: string): string {
  const dims = (DIMENSIONS as Record<string, ReadonlyArray<{ value: string; label: string }>>)[object]
  return dims?.find(d => d.value === groupBy)?.label ?? groupBy
}
function measureLabelFor(object: string, measure: string): string {
  const ms = (MEASURES as Record<string, ReadonlyArray<{ value: string; label: string }>>)[object]
  return ms?.find(m => m.value === measure)?.label ?? measure
}
function rangeFieldLabel(object: string, field: string): string {
  const rs = (RANGE_FIELDS as Record<string, ReadonlyArray<{ value: string; label: string }>>)[object]
  return rs?.find(r => r.value === field)?.label ?? field
}

/** The sentence, or null when the version is not one this bundle speaks —
 *  the caller renders the raw JSON alone then (E-5). Shape:
 *    "<Object> by <Dimension> · <Measure> · <Filter>: <value>, <value> · <Range> <from> → <to>"
 *  with "no filters" when there are none. Every unknown slug prints as
 *  itself; nothing is guessed. */
export function definitionSentence(def: SalesReportDefinition, version: number): string | null {
  if (version !== KNOWN_DEFINITION_VERSION) return null
  const object = String(def.object ?? '')
  const parts = [`${objectLabel(object)} by ${dimensionLabelFor(object, String(def.groupBy ?? ''))}`, measureLabelFor(object, String(def.measure ?? ''))]
  const filters = filtersInWords((def.filters ?? {}) as unknown as Record<string, unknown>, def.labels)
  parts.push(filters.length ? filters.join(' · ') : 'no filters')
  if (def.range && def.range.field) {
    parts.push(`${rangeFieldLabel(object, def.range.field)} ${def.range.from ?? 'open'} → ${def.range.to ?? 'open'}`)
  }
  return parts.join(' · ')
}

function filtersInWords(filters: Record<string, unknown>, labels?: Record<string, string>): string[] {
  const out: string[] = []
  for (const [key, raw] of Object.entries(filters)) {
    if (raw === undefined || raw === null || raw === '' || (Array.isArray(raw) && raw.length === 0)) continue
    const values = (Array.isArray(raw) ? raw : [raw]).map(v => filterValue(key, String(v), labels))
    out.push(`${FILTER_LABELS[key] ?? key}: ${values.join(', ')}`)
  }
  return out
}

/** The raw definition, pretty and stable, for the disclosure and for a
 *  support reply to paste. */
export function definitionJSON(def: unknown): string {
  return JSON.stringify(def, null, 2)
}

// ── E-8: paging ──────────────────────────────────────────────────────────────

/** "Showing 200 of 340 exports — the newest 200." — rendered ONLY while
 *  more exist than are on screen, from the wire's exact total. Null when
 *  everything is shown, or when the wire sent no total (never guessed). */
export function showingLine(shown: number, total: number | undefined): string | null {
  if (total === undefined || shown >= total) return null
  return `Showing ${shown} of ${total} exports — the newest ${shown}.`
}

/** Appends a page to the rows already on screen, dropping any id already
 *  present (a row that completed between two fetches is not two rows). */
export function appendPage(existing: DataExport[], next: DataExport[]): DataExport[] {
  const seen = new Set(existing.map(r => r.id))
  return [...existing, ...next.filter(r => !seen.has(r.id))]
}
